import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { ntxAPI } from '../../../API/axiosNeatex';

interface TaskFollower {
  id: string;
  taskId: string;
  userId: string;
  userName?: string;
  userName2?: string;
  createdAt?: string;
}

// ==================== Async Thunks ====================

// 🔵 جلب المتابعين لمهمة
export const getTaskFollowersByTaskId = createAsyncThunk(
  'TaskFollowers/getTaskFollowersByTaskId',
  async (taskId: string, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.get(`Task/GetTaskFollowerByTaskId?taskId=${taskId}`);
      if (data?.succeeded) return data.data;
      return [];
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// 🟢 إضافة متابع
export const addTaskFollower = createAsyncThunk(
  'TaskFollowers/addTaskFollower',
  async (item: Omit<TaskFollower, 'id' | 'userName' | 'createdAt'>, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post('Task/AddTaskFollower', item);
      if (data?.succeeded) return data.data;
      return rejectWithValue(data?.message || 'فشل في إضافة المتابع');
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// 🗑️ حذف متابع
export const deleteTaskFollower = createAsyncThunk(
  'TaskFollowers/deleteTaskFollower',
  async (id: string, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post(`Task/DeleteTaskFollower?Id=${id}`);
      if (data?.succeeded) return id;
      return rejectWithValue(data?.message || 'فشل في حذف المتابع');
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// ==================== Slice ====================

interface TaskFollowersState {
  taskFollowersList: TaskFollower[];
  loading: boolean;
  error: string | null;
}

const initialState: TaskFollowersState = {
  taskFollowersList: [],
  loading: false,
  error: null
};

const TaskFollowersSlice = createSlice({
  name: 'TaskFollowers',
  initialState,
  reducers: {
    clearTaskFollowers: state => {
      state.taskFollowersList = [];
      state.error = null;
      state.loading = false;
    },
    addLocalTaskFollower: (state, action: PayloadAction<TaskFollower>) => {
      state.taskFollowersList.unshift(action.payload);
    },
    removeLocalTaskFollower: (state, action: PayloadAction<string>) => {
      state.taskFollowersList = state.taskFollowersList.filter(f => f.id !== action.payload);
    }
  },
  extraReducers: builder => {
    builder
      // 🔄 Get
      .addCase(getTaskFollowersByTaskId.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTaskFollowersByTaskId.fulfilled, (state, action) => {
        state.loading = false;
        state.taskFollowersList = action.payload;
      })
      .addCase(getTaskFollowersByTaskId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // ➕ Add
      .addCase(addTaskFollower.fulfilled, (state, action) => {
        state.taskFollowersList.unshift(action.payload);
      })
      .addCase(addTaskFollower.rejected, (state, action) => {
        state.error = action.payload as string;
      })

      // 🗑️ Delete
      .addCase(deleteTaskFollower.fulfilled, (state, action) => {
        state.taskFollowersList = state.taskFollowersList.filter(f => f.id !== action.payload);
      })
      .addCase(deleteTaskFollower.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  }
});

export const { clearTaskFollowers, addLocalTaskFollower, removeLocalTaskFollower } = TaskFollowersSlice.actions;

export default TaskFollowersSlice.reducer;
